import React from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import moment from 'moment-timezone'
import { useFormik } from 'formik'

import { Profile } from 'common/enum/profile'
import { useStores } from 'presentation/hooks/use-stores'
import { ReactComponent as SearchIcon } from 'presentation/assets/icons/search.svg'
import Table from 'presentation/shared/components/Table'
import { Patient } from 'domain/entities/patient-model'
import { LoadPatients } from 'domain/usecases/patient/load-patients'
import { PaginationModel } from 'domain/entities/pagination-model'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import * as S from './styles'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'

type Props = {
  loadPatients: LoadPatients
  hasHeading?: boolean
} & WithLoadingProps

type PaginationState = {
  count: number
  itemsPerPage: number
}

function PatientsTable({
  hasHeading = true,
  loadPatients,
  setIsLoading
}: Props) {
  const history = useHistory()

  const [patientsData, setPatientsData] = React.useState<Patient[]>(
    [] as Patient[]
  )

  const [paginationState, setPaginationState] = React.useState<PaginationState>(
    {
      count: 10,
      itemsPerPage: 10
    }
  )
  const user = useStores().currentAccount?.getCurrentAccount()?.user

  async function loadPatientsPaginated(
    pageNumber: number,
    pageSize: number
  ): Promise<PaginationModel> {
    const pagination = {
      pageNumber,
      pageSize
    }

    const response = await findPatients({
      pagination,
      name: formik.values.name
    })

    setPaginationState({
      count: response?.pageInfo.totalItems || 10,
      itemsPerPage: response?.pageInfo.itemsPerPage || 10
    })

    return response ? response.pageInfo : ({} as PaginationModel)
  }

  const findPatients = async ({ name, pagination }: LoadPatients.Params) => {
    try {
      setIsLoading(true)
      const response = await loadPatients.load({
        name: name || '',
        pagination: {
          pageNumber: pagination?.pageNumber || 1,
          pageSize: pagination?.pageSize || 10
        },
        params: [
          'cpf',
          'email',
          'phone',
          'name',
          'gender',
          'patient_id',
          'birthday',
          'healthInsurance {healthInsuranceCode, healthInsuranceName}'
        ]
      })

      const { data, pageInfo } = response

      setPatientsData(data)

      if (pageInfo) {
        setPaginationState({
          count: response.pageInfo.totalItems || 10,
          itemsPerPage: response.pageInfo.itemsPerPage || 10
        })
      }

      return response
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setIsLoading(false)
    }
  }

  const findAllPatients = (): any => findPatients({} as LoadPatients.Params)

  React.useEffect(() => {
    ;(async () => await findAllPatients())()
  }, [])

  const formik = useFormik({
    initialValues: { name: '' },
    onSubmit: ({ name }) => {
      name
        ? findPatients({ name: name } as LoadPatients.Params)
        : findAllPatients()
    }
  })

  return (
    <S.Wrapper>
      {hasHeading && (
        <>
          <HeadingNew as="h1" size="xlarge" color="primary600">
            Pacientes
          </HeadingNew>
          <S.FilterWrapper>
            <S.SearchWrapper onSubmit={formik.handleSubmit}>
              <TextFieldNew
                onResetTableValue={findAllPatients}
                iconPosition="insideLeft"
                icon={<SearchIcon />}
                placeholder="Digite o nome do paciente"
                onInputChange={formik.handleChange('name')}
              />
            </S.SearchWrapper>
            <S.Filters>
              {(user.groups.includes(Profile.HOSPITAL) ||
                user.role === Profile.HOSPITAL) && (
                <S.ButtonAddPatient
                  onClick={() => history.push('/paciente/save')}
                >
                  <span>Adicionar paciente</span>
                </S.ButtonAddPatient>
              )}
            </S.Filters>
          </S.FilterWrapper>
        </>
      )}
      <Table
        pagination
        count={paginationState.count}
        onPaginate={loadPatientsPaginated}
        rowsPerPage={paginationState.itemsPerPage}
        columns={columns}
        data={patientsData?.map((patient) => ({
          cpf: patient.cpf,
          cod: patient.patient_id,
          name: patient.name,
          birthday: moment(patient.birthday).format('DD-MM-YYYY'),
          gender: patient.gender,
          insurance: patient.healthInsurance?.healthInsuranceName,
          email: patient.email,
          phone: patient.phone
        }))}
      />
    </S.Wrapper>
  )
}

export default WithLoading(PatientsTable)

const columns = [
  {
    name: 'cpf',
    label: 'CPF',
    options: {
      sort: false
    }
  },
  {
    name: 'cod',
    label: 'Cod. paciente',
    options: {
      sort: false
    }
  },
  {
    name: 'name',
    label: 'Paciente',
    options: {
      sort: false
    }
  },
  {
    name: 'birthday',
    label: 'Dt. de nascimento',
    options: {
      sort: false
    }
  },
  {
    name: 'gender',
    label: 'Sexo',
    options: {
      sort: false
    }
  },
  {
    name: 'insurance',
    label: 'Convênio',
    options: {
      sort: false
    }
  },
  {
    name: 'email',
    label: 'E-mail',
    options: {
      sort: false
    }
  },
  {
    name: 'phone',
    label: 'Telefone',
    options: {
      sort: false
    }
  }
]
